// The frame around an app: the header at the top and the footer at the bottom.
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";

/**
 * <i80-app-header home-href="/" home-text="zPlan" home-icon="i80">
 *   <i80-button slot="global" color="tertiary" href="/plans">Plans</i80-button>
 *   <i80-dropdown slot="utility" text="Ada"></i80-dropdown>
 * </i80-app-header>
 *
 * Below the breakpoint the actions collapse behind a Menu button, as in Helios. Children
 * with slot="global" are the main navigation, slot="utility" the account side.
 */
export class I80AppHeader extends I80Element {
  static get observedAttributes() {
    return ["home-href", "home-text", "home-icon", "home-icon-only", "breakpoint", "open"];
  }

  setup() {
    let root = this.part("header");
    if (!root) {
      const slot = (name) => [...this.children].filter((c) => c.getAttribute("slot") === name);
      const global = slot("global");
      const utility = slot("utility");
      this.takeContent([...global, ...utility]).forEach((n) => n.remove());
      root = h("div", { "data-i80-part": "header" },
        h("div", { class: "i80-app-header__actions", "data-i80-part": "actions" },
          h("div", { class: "i80-app-header__global-actions" }, global),
          h("div", { class: "i80-app-header__utility-actions" }, utility)));
      this.append(root);
    }
    this._root = root;
    this._actions = root.querySelector('[data-i80-part="actions"]');
    this._actions.id = nextId("app-header-menu");

    this._global = root.querySelector(".i80-app-header__global-actions");
    this._utility = root.querySelector(".i80-app-header__utility-actions");

    const width = parseInt(this.getAttribute("breakpoint") ?? "768", 10) || 768;
    this._media = window.matchMedia(`(min-width: ${width}px)`);
    this._onChange = () => this.update();
    this._media.addEventListener("change", this._onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._media?.removeEventListener("change", this._onChange);
  }

  adopt() {
    for (const node of this.takeContent()) {
      if (node.nodeType !== 1) continue;
      (node.getAttribute("slot") === "utility" ? this._utility : this._global).append(node);
    }
  }

  get isDesktop() {
    return this._media?.matches ?? true;
  }

  toggle(force) {
    this.toggleAttribute("open", force ?? !this.flag("open"));
  }

  update() {
    const desktop = this.isDesktop;
    const open = this.flag("open");
    this._root.className = [
      "i80-app-header",
      desktop ? "i80-app-header--is-desktop" : "i80-app-header--is-mobile",
      !desktop && (open ? "i80-app-header--menu-is-open" : "i80-app-header--menu-is-closed"),
    ].filter(Boolean).join(" ");

    // Home link
    const href = this.getAttribute("home-href");
    const text = this.getAttribute("home-text") ?? "";
    const name = this.getAttribute("home-icon");
    const home = h(href ? "a" : "div", { class: "i80-app-header__home-link", href, "aria-label": text && !name ? null : text });
    if (name) home.append(icon(name, { stretched: true }));
    if (text && !this.flag("home-icon-only")) home.append(h("div", { class: "i80-text i80-typography-display-100 i80-font-weight-medium i80-foreground-high-contrast" }, text));
    this._root.querySelector(":scope > .i80-app-header__home-link")?.remove();
    this._root.prepend(home);

    // Menu button, on mobile only
    this._root.querySelector(":scope > .i80-app-header__menu-button")?.remove();
    if (!desktop) {
      const button = h("button", { type: "button", class: "i80-button i80-button--color-tertiary i80-button--size-medium i80-app-header__menu-button", "aria-controls": this._actions.id, "aria-expanded": String(open) },
        h("span", { class: "i80-button__text" }, "Menu"),
        h("span", { class: "i80-button__icon" }, icon(open ? "x" : "menu", { stretched: true })));
      button.addEventListener("click", () => this.toggle());
      this._actions.before(button);
    }
  }
}

/**
 * <i80-app-footer year="2026" theme="light">
 *   <a href="/support">Support</a>
 *   <a href="/terms">Terms</a>
 * </i80-app-footer>
 * Links you write inside become footer links in a list, with the copyright line last.
 */
export class I80AppFooter extends I80Element {
  static get observedAttributes() {
    return ["theme", "year", "aria-label", "copyright"];
  }

  setup() {
    let root = this.part("footer");
    if (!root) {
      const links = this.takeContent();
      root = h("div", { "data-i80-part": "footer" },
        h("ul", { class: "i80-app-footer__list", "data-i80-part": "list" },
          links.map((link) => h("li", { class: "i80-app-footer__list-item" }, link))));
      this.append(root);
    }
    this._root = root;
  }

  adopt() {
    const list = this._root.querySelector('[data-i80-part="list"]');
    for (const node of this.takeContent()) list.append(h("li", { class: "i80-app-footer__list-item" }, node));
  }

  update() {
    this._root.className = `i80-app-footer i80-app-footer--theme-${this.choice("theme", ["light", "dark"], "light")}`;
    const list = this._root.querySelector('[data-i80-part="list"]');
    list.setAttribute("aria-label", this.getAttribute("aria-label") ?? "Footer");
    for (const link of list.querySelectorAll("li > a:not([class])")) {
      link.className = "i80-link-inline i80-link-inline--color-secondary i80-app-footer__link i80-text i80-typography-body-100 i80-font-weight-regular";
    }
    const year = this.getAttribute("year") ?? String(new Date().getFullYear());
    const text = this.getAttribute("copyright") ?? `© ${year} i80`;
    const copyright = this._root.querySelector(":scope > .i80-app-footer__copyright")
      ?? this._root.appendChild(h("div", { class: "i80-app-footer__copyright" }, h("span", { class: "i80-text i80-typography-body-100 i80-font-weight-regular" })));
    copyright.firstElementChild.textContent = text;
  }
}

/**
 * <i80-app-side-nav>
 *   <i80-nav-title>Plans</i80-nav-title>
 *   <i80-nav-link href="/plans" icon="folder" count="12" active>All plans</i80-nav-link>
 *   <i80-nav-link href="/archive" icon="archive">Archive</i80-nav-link>
 * </i80-app-side-nav>
 *
 * The local navigation down the side. On a wide screen the toggle button minimises it;
 * below the breakpoint it slides in over the page as a dialog, closed by the overlay
 * or Escape. Fires i80-toggle with {minimized} and i80-open/i80-close on mobile.
 */
export class I80AppSideNav extends I80Element {
  static get observedAttributes() {
    return ["minimized", "open", "responsive", "breakpoint", "label"];
  }

  setup() {
    let root = this.part("side-nav");
    if (!root) {
      const items = this.takeContent();
      root = h("div", { "data-i80-part": "side-nav" },
        h("h2", { class: "sr-only", "data-i80-part": "label" }),
        h("div", { class: "i80-app-side-nav__wrapper" },
          h("div", { class: "i80-app-side-nav__overlay", "data-i80-part": "overlay" }),
          h("button", { type: "button", class: "i80-app-side-nav__toggle-button", "data-i80-part": "toggle" }),
          h("div", { class: "i80-app-side-nav__wrapper-body" },
            h("nav", { class: "i80-app-side-nav__list-wrapper", "data-i80-part": "nav" },
              h("ul", { class: "i80-app-side-nav__list", role: "list", "data-i80-part": "list" }, items)))));
      this.append(root);
    }
    this._root = root;
    root.querySelector('[data-i80-part="label"]').id = nextId("side-nav-label");
    root.querySelector('[data-i80-part="toggle"]').addEventListener("click", () => this.toggle());
    root.querySelector('[data-i80-part="overlay"]').addEventListener("click", () => this.close());
    this._onKey = (e) => {
      if (e.key === "Escape" && !this.isDesktop && this.flag("open")) this.close();
    };
    this.addEventListener("keydown", this._onKey);

    this._list = root.querySelector('[data-i80-part="list"]');

    const width = parseInt(this.getAttribute("breakpoint") ?? "1088", 10) || 1088;
    this._media = window.matchMedia(`(min-width: ${width}px)`);
    this._onChange = () => this.update();
    this._media.addEventListener("change", this._onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._media?.removeEventListener("change", this._onChange);
  }

  adopt() {
    this._list.append(...this.takeContent());
  }

  get isDesktop() {
    return this._media?.matches ?? true;
  }

  get responsive() {
    return !this.hasAttribute("responsive") || this.flag("responsive");
  }

  /** Minimise on a wide screen, open or close the panel on a narrow one. */
  toggle(force) {
    if (!this.isDesktop) {
      const open = force ?? !this.flag("open");
      this.toggleAttribute("open", open);
      this.dispatchEvent(new CustomEvent(open ? "i80-open" : "i80-close", { bubbles: true }));
      return;
    }
    const minimized = force ?? !this.flag("minimized");
    this.toggleAttribute("minimized", minimized);
    this.dispatchEvent(new CustomEvent("i80-toggle", { bubbles: true, detail: { minimized } }));
  }

  close() {
    if (!this.isDesktop) this.toggle(false);
  }

  update() {
    const desktop = this.isDesktop;
    const responsive = this.responsive;
    const minimized = responsive && (desktop ? this.flag("minimized") : !this.flag("open"));
    this._root.className = [
      "i80-app-side-nav",
      responsive && "i80-app-side-nav--is-responsive",
      responsive && (desktop ? "i80-app-side-nav--is-desktop" : "i80-app-side-nav--is-mobile"),
      responsive && (minimized ? "i80-app-side-nav--is-minimized" : "i80-app-side-nav--is-not-minimized"),
    ].filter(Boolean).join(" ");

    const label = this._root.querySelector('[data-i80-part="label"]');
    label.textContent = this.getAttribute("label") ?? "Application local navigation";
    this._root.querySelector('[data-i80-part="nav"]').setAttribute("aria-label", label.textContent);

    // On a narrow screen the panel is a dialog over the page.
    if (responsive && !desktop) {
      this._root.setAttribute("role", "dialog");
      this._root.setAttribute("aria-modal", "true");
      this._root.setAttribute("aria-labelledby", label.id);
    } else {
      for (const a of ["role", "aria-modal", "aria-labelledby"]) this._root.removeAttribute(a);
    }

    const toggle = this._root.querySelector('[data-i80-part="toggle"]');
    toggle.hidden = !responsive;
    toggle.setAttribute("aria-labelledby", label.id);
    toggle.setAttribute("aria-expanded", String(!minimized));
    toggle.replaceChildren(icon(minimized ? "chevrons-right" : "chevrons-left"));

    // <ul> may only contain <li>, so each entry is wrapped once.
    for (const entry of this._root.querySelectorAll("i80-nav-link, i80-nav-title")) {
      if (!entry.parentElement?.matches("li")) {
        const item = h("li", { class: "i80-app-side-nav__list-item" });
        entry.before(item);
        item.append(entry);
      }
      entry.parentElement.className = "i80-app-side-nav__list-item";
      entry.update?.();
    }
  }
}

/** One link in the side navigation. `active` marks the page you are on. */
export class I80NavLink extends I80Element {
  static get observedAttributes() {
    return ["href", "icon", "count", "badge", "active", "external", "back"];
  }

  setup() {
    const content = this.part("link") ?? h("span", { "data-i80-part": "link" }, this.takeContent());
    if (!content.isConnected) this.append(content);
    this._label = this.querySelector(".i80-app-side-nav__list-item-text")
      ?? h("span", { class: "i80-app-side-nav__list-item-text i80-typography-body-200 i80-font-weight-medium" }, ...content.childNodes);
  }

  adopt() {
    for (const node of this.takeContent()) this._label.append(node);
  }

  update() {
    const href = this.getAttribute("href");
    const active = this.flag("active");
    const back = this.flag("back");
    const name = back ? "chevron-left" : this.getAttribute("icon");
    const external = this.flag("external");
    const link = h(href ? "a" : "button", {
      class: ["i80-app-side-nav__list-item-link", active && "active", back && "i80-app-side-nav__list-item-link--back-link"].filter(Boolean).join(" "),
      "data-i80-part": "link", href, type: href ? null : "button",
      "aria-current": active ? "page" : null,
      target: external ? "_blank" : null, rel: external ? "noopener noreferrer" : null,
    });
    if (name) link.append(icon(name, { className: "i80-app-side-nav__list-item-icon-leading" }));
    link.append(this._label);
    const count = this.getAttribute("count");
    if (count) link.append(h("div", { class: "i80-badge-count i80-badge-count--size-small i80-badge-count--type-outlined i80-badge-count--color-neutral" }, count));
    const badge = this.getAttribute("badge");
    if (badge) link.append(h("div", { class: "i80-badge i80-badge--size-small i80-badge--type-outlined i80-badge--color-neutral" }, h("div", { class: "i80-badge__text" }, badge)));
    if (external) link.append(h("span", { class: "i80-app-side-nav__list-item-icon-trailing" }, icon("external-link")));

    this.querySelector('[data-i80-part="link"]').replaceWith(link);
  }
}

/** A heading over a group of links. */
export class I80NavTitle extends I80Element {
  setup() {
    const title = this.part("title") ?? h("div", { "data-i80-part": "title" }, this.takeContent());
    if (!title.isConnected) this.append(title);
  }

  adopt() {
    this.querySelector('[data-i80-part="title"]').append(...this.takeContent());
  }

  update() {
    this.querySelector('[data-i80-part="title"]').className = "i80-app-side-nav__list-title i80-typography-body-100 i80-font-weight-semibold";
  }
}

define("i80-app-side-nav", I80AppSideNav);
define("i80-nav-link", I80NavLink);
define("i80-nav-title", I80NavTitle);
define("i80-app-header", I80AppHeader);
define("i80-app-footer", I80AppFooter);
